import BaseDashboardCard from "../BaseDashboardCard";
import { t } from "i18next";
import Box from "../Box";
import DeltaBadge from "../DeltaBadge";
import { CommercialUnitDataWithBrand } from "../../types/commercial";
import PieChart from "../PieChart";
import styled from "styled-components";
import MrclText from "../marcel/MrclText";
import { formatCurrency, formatPercentage } from "../../utils/numberUtils";
import useBreakPoint from "../../utils/useBreakPoint";

type TurnoverTriptychCardDesktopProps = {
  title: string;
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: () => void;
  triptychTurnoverRates: (CommercialUnitDataWithBrand & {evol: number | null } )[];

};

const TurnoverTriptychCardDesktop = ({title, openInformationModal,triptychTurnoverRates,  ...rest }: TurnoverTriptychCardDesktopProps) => {
  const colors = [ "#2B72FB","#EECA34", "#64BDC6"];
  const isBreak = useBreakPoint();
   const isLandscape =
      typeof window !== "undefined" &&
      Boolean(window.matchMedia?.("(max-width: 932px) and (orientation: landscape)")?.matches);
    
  const data:  {
      key: string,
      y: number,
      label?: string,
    }[] = triptychTurnoverRates?.map(x => ({
      key: x.brand ?? x.slice ?? "", 
      y: (x.turnoverRate ?? 0 ) * 100,
      label: `${t(`commercial.NEW_${x.brand}`)}\n${formatPercentage((x.turnoverRate ?? 0) * 100)}`
    })) || [];


  return (
    <StyledBaseDashboardCard
      title={t(title)}
      size={"small"}
      hasModal={true}
      openInformationModal={openInformationModal}
      width="100%"
      height="100%"
      p="16px"
      mb={0}
      {...rest}
      importedDateTime={ triptychTurnoverRates?.length ? triptychTurnoverRates[0]?.importedDateTime : undefined }
    >
      <BoxContainer display="flex" flexDirection="column" alignItems="center">
        <PieChart
          colors={colors}
          data={data}
          showTooltip={true}
        />
        <Table>
          <tbody>
            {triptychTurnoverRates?.map((item, index) => (
              <Tr key={`${item.brand ?? item.slice ?? index}`}>
                <Td>
                  <Box display="flex" alignItems="center">
                    <Bull style={{ backgroundColor: colors[index] }} />
                    <Marque>
                      {(isBreak === "phone" || isLandscape)
                        ? item.brand
                        : t(`commercial.NEW_${item.brand}`)}
                    </Marque>
                  
                  </Box>
                </Td>
                <Td>
                  <Text size="m" >{formatPercentage((item.turnoverRate ?? 0) * 100)}</Text>
                </Td>
                <Td>
                  <Text size="m"  color="#545454">{formatCurrency(item.turnover ?? 0)}</Text>
                </Td>
                <Td>
                    <DeltaBadge value={item.evol} showIcon={false} showSign={true} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </BoxContainer>
    </StyledBaseDashboardCard>
  );
};

const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 11px;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    margin-top: 4px;
    font-size: 9px;
    max-width: 320px;
  }
`;
const Tr = styled.tr`
height:20px`;

const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 4px;
`;

const BoxContainer = styled(Box)`
  gap: 8px;
  margin-top: 8px;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    margin-top: 0 !important;
    flex: none !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;
    align-items: center !important;
    width: 100%;
  }
`;
const Text = styled(MrclText)<{ bold?: boolean; color?: string }>`
  font-size: 14px !important;
  font-weight: ${(props) => (props.bold ? "700" : "400")} !important;
  color: ${(props) => props.color || "inherit"};

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    font-size: 14px !important;
  }
`;

const Marque = styled.span`
  font-size: 12px;
  font-weight: 700;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    font-size: 14px;
  }
`;
const Bull = styled.span`
  border-radius: 8px;
  width: 10px;
  height: 10px;
  display: inline-block;
  margin-right: 6px;
`;

const StyledBaseDashboardCard = styled(BaseDashboardCard)<{ isLoading?: boolean; isUnavailable?: boolean }>`
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding: 4px 8px !important;

    /* Force intermediate elements to take full height to allow vertical centering only when not loading or unavailable */
    ${({ isLoading, isUnavailable }) => !isLoading && !isUnavailable && `
      & > div,
      .c-card {
        display: flex !important;
        flex-direction: column !important;
        height: auto !important;
        flex: 1 !important;
      }

      [class*="content"],
      [class*="body"] {
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        flex: 1 !important;
        padding-bottom: 0px !important;
        height: auto !important;
      }
    `}
  }
`;

export default TurnoverTriptychCardDesktop;
